import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/lib/auth-context';
import DynamicToolRunner from '@/components/tools/DynamicToolRunner';
import type { AiTool, PortalRole } from '@/types/aiTools';
import { ROLE_SLOT_CONFIG } from '@/types/aiTools';

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

type RoleActiveToolsProps = {
  role: PortalRole;
  title: string;
  description: string;
  marketplacePath: string;
  /** Path prefix for SmartBlock tracker pages (e.g. /portal/locker/tracker) */
  trackerBasePath?: string;
  /** Optional: Show coming soon message for tools that aren't implemented yet */
  showComingSoon?: boolean;
};

export function RoleActiveTools({
  role,
  title,
  description,
  marketplacePath,
  trackerBasePath,
  showComingSoon = true,
}: RoleActiveToolsProps) {
  const { session } = useAuthContext() as { session: { user?: { id: string }; access_token?: string } | null };
  const accessToken = session?.access_token ?? null;

  const slotConfig = ROLE_SLOT_CONFIG[role];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTools, setActiveTools] = useState<AiTool[]>([]);

  const loadActiveTools = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${CORE_API}/api/v1/marketplace/me-tools?role=${role}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error?.message || body.error || `Failed to load tools (${res.status})`);
      }
      const data = await res.json();
      const payload = data.data ?? data;
      // Filter to only enabled tools
      const enabled = (payload.tools || []).filter((t: any) => t.enabled);
      // me-tools returns { tool_id, meta, tc_cost, ... } — normalize to AiTool shape with `id`
      setActiveTools(enabled.map((t: any) => {
        const base = t.tool || t;
        return { ...base, id: base.id || t.tool_id };
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load tools';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, role]);

  useEffect(() => {
    loadActiveTools();
  }, [loadActiveTools]);

  const totalSlots = slotConfig.total;

  if (loading) {
    return (
      <div className="space-y-6 p-4">
        <h1 className="text-2xl font-semibold text-cocoa">{title}</h1>
        <div className="text-sm text-cocoa/70">Loading your tools...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-cocoa">{title}</h1>
          <p className="mt-1 text-sm text-cocoa/70">{description}</p>
        </div>
        <a
          href={marketplacePath}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 transition-colors"
        >
          Browse Marketplace
        </a>
      </div>

      {/* Active Tools Count */}
      <div className="flex items-center gap-2 text-sm text-cocoa/70">
        <span className="font-medium text-cocoa">{activeTools.length}</span> of {totalSlots} tool slots active
        {slotConfig.universalSlots && (
          <span className="text-xs text-cocoa/50">(AI &amp; SmartBlock tools share slots)</span>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-lg border border-brick/30 bg-brick/10 p-3 text-sm text-brick">
          {error}
        </div>
      )}

      {/* Active Tools Grid */}
      {activeTools.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
            <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-cocoa">No active tools yet</h3>
          <p className="mt-1 text-sm text-slate-500">
            Visit the marketplace to add tools to your toolkit.
          </p>
          <a
            href={marketplacePath}
            className="mt-4 inline-block rounded-lg bg-sky-600 px-6 py-2 text-sm font-medium text-white hover:bg-sky-700"
          >
            Browse Marketplace
          </a>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {activeTools.map((tool) => (
            <ToolLauncherCard
              key={tool.id}
              tool={tool}
              showComingSoon={showComingSoon}
              trackerBasePath={trackerBasePath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type ToolLauncherCardProps = {
  tool: AiTool;
  showComingSoon?: boolean;
  trackerBasePath?: string;
};

function ToolLauncherCard({ tool, showComingSoon = true, trackerBasePath }: ToolLauncherCardProps) {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const slotType = tool.meta?.slot_type || 'ai';
  const isSmartBlock = slotType === 'gec0';
  const trackerTypeId = tool.meta?.tracker_type_id;

  const handleClick = () => {
    // SmartBlock tools with a tracker_type_id navigate to the tracker page
    if (isSmartBlock && trackerTypeId && trackerBasePath) {
      navigate(`${trackerBasePath}?type=${trackerTypeId}`);
      return;
    }
    setShowModal(true);
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="group relative flex flex-col rounded-xl border-2 border-slate-200 bg-white p-4 text-left transition-all hover:border-sky-300 hover:shadow-md"
      >
        {/* Tool Icon & Info */}
        <div className="mb-3 flex items-center gap-3">
          {tool.meta?.icon_url ? (
            <img src={tool.meta.icon_url} alt="" className="h-12 w-12 rounded-xl object-cover shadow-sm" />
          ) : (
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-xl ${
                slotType === 'gec0' ? 'bg-emerald-200 text-emerald-800' : 'bg-sky-200 text-sky-800'
              }`}
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-cocoa truncate">
              {tool.meta?.label || tool.meta?.name || tool.meta?.summary || tool.id}
            </h3>
            <span
              className={`inline-block mt-0.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                slotType === 'gec0'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-sky-100 text-sky-700'
              }`}
            >
              {slotType === 'gec0' ? 'SmartBlock' : 'AI Tool'}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="mb-3 text-sm text-cocoa/70 line-clamp-2">
          {tool.meta?.description || tool.meta?.surface_hint || 'AI-powered tool'}
        </p>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between">
          {(tool.tc_cost ?? 0) > 0 && (tool.remaining_uses ?? 0) > 0 ? (
            <span className="text-xs text-sky-700 font-medium">
              {tool.remaining_uses}/{tool.uses_per_pack ?? 1} uses
            </span>
          ) : (tool.tc_cost ?? 0) > 0 ? (
            <span className="text-xs text-amber-700 font-medium">
              {tool.tc_cost} TC &rarr; {tool.uses_per_pack ?? 1} uses
            </span>
          ) : (
            <span className="text-xs text-emerald-600 font-medium">Free</span>
          )}
          <span className="text-xs font-medium text-sky-600 group-hover:text-sky-700">
            Launch &rarr;
          </span>
        </div>
      </button>

      {/* Tool Runner Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowModal(false)}>
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <DynamicToolRunner
              toolId={tool.id}
              accentColor={slotType === 'gec0' ? '#059669' : '#0284c7'}
              bgColor="#ffffff"
              textColor="#3E2723"
              initialRemainingUses={tool.remaining_uses ?? 0}
              onClose={() => setShowModal(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default RoleActiveTools;
